import React from 'react';
import { Link } from 'react-router-dom';
import { Bar, BarChart, CartesianGrid, Cell, Legend, ResponsiveContainer, Tooltip as ChartTooltip, XAxis, YAxis } from 'recharts';
import { ArrowUpRight } from 'lucide-react';
import { PageHeader } from '../components/common/PageHeader';
import { KpiCard } from '../components/common/KpiCard';
import { SectionCard } from '../components/common/SectionCard';
import { AiDailyBrief } from '../components/ai/AiDailyBrief';
import { ScoreChip } from '../components/common/ScoreChip';
import { GateStatusPill } from '../components/common/StatusPills';
import { usePlatform } from '../contexts/PlatformContext';
import { MEGA_MAJOR_STAGES, STAGE_META, summariseGate } from '../utils/stages';
import { formatAed, formatDate, daysUntil, formatCompact } from '../utils/format';
import { entityName } from '../data/entities';
import { campaigns } from '../data/outreach';
import { IntakeTrendChart } from '../components/dashboard/IntakeTrendChart';
import { TrackMix } from '../components/dashboard/TrackMix';
import { SupportByStage } from '../components/dashboard/SupportByStage';
import { RiskWatchlist } from '../components/dashboard/RiskWatchlist';
import { OutreachSnapshot } from '../components/dashboard/OutreachSnapshot';
import { OfficerWorkload } from '../components/dashboard/OfficerWorkload';
import { ActivityFeed } from '../components/dashboard/ActivityFeed';
export function Dashboard() {
  const {
    applications
  } = usePlatform();
  const live = applications.filter(record => record.status !== 'rejected');
  const megaMajor = live.filter(record => record.track !== 'international');
  const international = live.filter(record => record.track === 'international');
  const committed = live.filter(record => record.stage !== 'assessment').reduce((sum, record) => sum + record.requestedSupportAed, 0);
  const meanScore = live.reduce((sum, record) => sum + record.aiScore, 0) / Math.max(live.length, 1);
  const stageData = MEGA_MAJOR_STAGES.map(stage => ({
    stage: STAGE_META[stage].label,
    Mega: megaMajor.filter(r => r.track === 'mega' && r.stage === stage).length,
    Major: megaMajor.filter(r => r.track === 'major' && r.stage === stage).length
  }));
  const emirates = Array.from(new Set(live.map(record => record.emirate)));
  const emirateData = emirates.map(emirate => ({
    emirate,
    records: live.filter(record => record.emirate === emirate).length
  })).sort((a, b) => b.records - a.records);
  const gatesDue = live.map(record => ({
    record,
    summary: summariseGate(record.gates[record.stage] ?? [])
  })).filter(entry => entry.summary.total > 0 && !entry.summary.cleared).sort((a, b) => daysUntil(a.record.slaDueOn) - daysUntil(b.record.slaDueOn)).slice(0, 5);
  const slaDueSoon = live.filter(record => {
    const days = daysUntil(record.slaDueOn);
    return days >= 0 && days <= 7;
  }).length;
  const slaOverdue = live.filter(record => daysUntil(record.slaDueOn) < 0).length;
  const allFlags = live.flatMap(record => record.flags);
  const criticalFlags = allFlags.filter(flag => flag.severity === 'critical').length;
  const highFlags = allFlags.filter(flag => flag.severity === 'high').length;
  const pendingRecs = live.reduce((sum, record) => sum + record.recommendations.filter(item => item.status === 'pending').length, 0);
  const impressions = campaigns.reduce((sum, campaign) => sum + campaign.impressions, 0);
  const entitiesEngaged = new Set(live.map(record => record.entityId)).size;
  const allDocs = live.flatMap(record => record.documents);
  const docsMissing = allDocs.filter(doc => doc.status === 'missing').length;
  const docsVerified = allDocs.filter(doc => doc.status === 'verified').length;
  const deadlines = live.filter(record => record.bid?.submissionDeadline).map(record => ({
    record,
    on: record.bid!.submissionDeadline
  })).sort((a, b) => daysUntil(a.on) - daysUntil(b.on));
  return <div data-id="element-1347" data-mp="2vdf6ohy57m8q">
      <PageHeader eyebrow="Command dashboard" title="Events support portfolio" description="Live position across the Mega and Major six-stage lifecycle and the International outreach track." actions={<Link to="/applications" className="inline-flex items-center rounded-sm bg-uae-green px-3 py-2 text-[13px] font-medium text-white transition-colors hover:bg-uae-leaf" data-id="element-1349" data-mp="2getgweufhq7n">
            Open applications queue
            <ArrowUpRight className="ml-1.5 h-3.5 w-3.5" aria-hidden="true" data-id="element-1350" data-mp="393r14ni79x31" />
          </Link>} data-id="element-1348" data-mp="2vd5goud3lf31" />

      <div className="space-y-4 p-6" data-id="element-1351" data-mp="34apj46mp5hf8">
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-5" data-id="element-1352" data-mp="2zdp9jlqn1wjd">
          <KpiCard label="Records in pipeline" value={String(live.length)} accent="green" meta={`${megaMajor.length} Mega & Major · ${international.length} International`} data-id="element-1353" data-mp="2zbcx1o3ngedp" />
          <KpiCard label="Active bids" value={String(megaMajor.filter(r => r.stage === 'bid').length)} accent="leaf" meta="Stage 2 — host-body submission in progress" data-id="element-1354" data-mp="45mmtr6fo5lb" />
          <KpiCard label="In delivery" value={String(megaMajor.filter(r => r.stage === 'readiness' || r.stage === 'deliver-operate').length)} accent="gold" meta="Readiness and live operations" data-id="element-1355" data-mp="d4zv4gitmo4g" />
          <KpiCard label="Support committed" value={formatAed(committed)} accent="navy" meta="Across records past Assessment" data-id="element-1356" data-mp="r4r8cf1ik0he" />
          <KpiCard label="Mean AI score" value={meanScore.toFixed(1)} accent="sky" meta="Weighted across live records" data-id="element-1357" data-mp="10vvmjcjd7hb6" />
        </div>

        <div className="grid gap-3 sm:grid-cols-3 xl:grid-cols-6" data-id="element-1358" data-mp="1at9yayucfhx5">
          <KpiCard label="SLA due ≤ 7 days" value={String(slaDueSoon)} accent="gold" tone={slaDueSoon > 0 ? 'warn' : 'default'} meta={`${slaOverdue} already overdue`} data-id="element-1359" data-mp="1arfczjf5tnsj" />
          <KpiCard label="Critical AI flags" value={String(criticalFlags)} accent="red" tone={criticalFlags > 0 ? 'risk' : 'default'} meta={`${highFlags} high-severity`} data-id="element-1360" data-mp="uvpffj8bivzn" />
          <KpiCard label="Pending recommendations" value={String(pendingRecs)} accent="navy" meta="Awaiting officer accept / override" data-id="element-1361" data-mp="13y8io4g0namf" />
          <KpiCard label="Outreach impressions" value={formatCompact(impressions)} accent="leaf" meta={`${campaigns.length} live campaigns`} data-id="element-1362" data-mp="1dtjwmkjlodt4" />
          <KpiCard label="Entities engaged" value={String(entitiesEngaged)} accent="green" meta={`${emirates.length} emirates represented`} data-id="element-1363" data-mp="3flyb19mxr04s" />
          <KpiCard label="Documents missing" value={String(docsMissing)} accent="sky" meta={`${docsVerified} verified across records`} data-id="element-1364" data-mp="1q5cif7f251le" />
        </div>

        <div className="grid gap-4 xl:grid-cols-[1.5fr_1fr]" data-id="element-1365" data-mp="1gcn9okaw61tr">
          <SectionCard title="Stage distribution — Mega & Major lifecycle" description="Where every record sits across the six governed stages" data-id="element-1366" data-mp="1lcjgssevy9dj">
            <div className="h-64 w-full" data-id="element-1367" data-mp="1bfwwc4qh1kc1">
              <ResponsiveContainer width="100%" height="100%" data-id="element-1368" data-mp="1bgzki4wet8o9">
                <BarChart data={stageData} margin={{
                top: 4,
                right: 8,
                left: -18,
                bottom: 0
              }} data-id="element-1369" data-mp="xgx7avz1r3fc">
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} data-id="element-1370" data-mp="12g1hnpxpj779" />
                  <XAxis dataKey="stage" tick={{
                  fontSize: 11,
                  fill: 'var(--muted-foreground)'
                }} interval={0} tickLine={false} axisLine={{
                  stroke: 'var(--border)'
                }} data-id="element-1371" data-mp="2ruy6jhyidsfy" />
                  <YAxis allowDecimals={false} tick={{
                  fontSize: 11,
                  fill: 'var(--muted-foreground)'
                }} tickLine={false} axisLine={false} data-id="element-1372" data-mp="1fk1i4c5bdqwo" />
                  <ChartTooltip contentStyle={{
                  fontSize: 12,
                  borderRadius: 4,
                  border: '1px solid var(--border)'
                }} data-id="element-1373" data-mp="15u8ebxbeyg2z" />
                  <Legend wrapperStyle={{
                  fontSize: 11
                }} data-id="element-1374" data-mp="rup8jb62rgtu" />
                  <Bar dataKey="Mega" stackId="a" fill="var(--chart-1)" data-id="element-1375" data-mp="rv8s0wbdid9z" />
                  <Bar dataKey="Major" stackId="a" fill="var(--chart-3)" data-id="element-1376" data-mp="mwxh0i9cdwq6" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </SectionCard>

          <AiDailyBrief data-id="element-1377" data-mp="2h9dm3fqej4tg" />
        </div>

        <div className="grid gap-4 xl:grid-cols-[1.5fr_1fr]" data-id="element-1378" data-mp="2hag93cklsn43">
          <IntakeTrendChart data-id="element-1379" data-mp="2otxuywlblp3t" />
          <TrackMix records={live} data-id="element-1380" data-mp="2oswluxnv7coq" />
        </div>

        <div className="grid gap-4 xl:grid-cols-2" data-id="element-1381" data-mp="2ts96rcv6e9oo">
          <SectionCard title="Gate decisions requiring attention" description="Current-stage gates that are not yet cleared" bodyClassName="p-0" data-id="element-1382" data-mp="2tqy4xilajc07">
            <ul className="divide-y divide-border" data-id="element-1383" data-mp="33hsqslumk9l4">
              {gatesDue.map(({
              record,
              summary
            }) => <li key={record.id} data-id="element-1384" data-mp="25wembu2ry8hx">
                  <Link to={record.track === 'international' ? `/applications/${record.id}` : `/lifecycle/${record.id}`} className="flex items-center justify-between gap-3 px-4 py-3 hover:bg-muted" data-id="element-1385" data-mp="2atese4zmjb91">
                    <div className="min-w-0" data-id="element-1386" data-mp="3r7dax82ilcvz">
                      <p className="truncate text-[13px] font-medium" data-id="element-1387" data-mp="3v98bgacmfm8p">{record.eventName}</p>
                      <p className="text-[11px] text-muted-foreground" data-id="element-1388" data-mp="1l4t2fkp3w8fk">
                        {STAGE_META[record.stage].label} gate · {summary.percent}% ready ·{' '}
                        {summary.outstanding} outstanding
                      </p>
                    </div>
                    <div className="flex shrink-0 items-center gap-2" data-id="element-1389" data-mp="1uwpejzo11z2s">
                      <ScoreChip score={record.aiScore} data-id="element-1390" data-mp="1zse7vl9gejqn" />
                      <GateStatusPill status={summary.outstanding > 0 ? 'outstanding' : 'partial'} data-id="element-1391" data-mp="12673och2atet" />
                    </div>
                  </Link>
                </li>)}
            </ul>
          </SectionCard>

          <SectionCard title="Records by emirate" description="Distribution of live records across the federation" data-id="element-1392" data-mp="1c67qi8e53rnr">
            <div className="h-56 w-full" data-id="element-1393" data-mp="2ni8hltyh7s6o">
              <ResponsiveContainer width="100%" height="100%" data-id="element-1394" data-mp="2nle5x6za6q1c">
                <BarChart data={emirateData} layout="vertical" margin={{
                top: 0,
                right: 12,
                left: 40,
                bottom: 0
              }} data-id="element-1395" data-mp="2shbkp8uwboc0">
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" horizontal={false} data-id="element-1396" data-mp="bvol6b2p67yp" />
                  <XAxis type="number" allowDecimals={false} tick={{
                  fontSize: 11,
                  fill: 'var(--muted-foreground)'
                }} tickLine={false} axisLine={false} data-id="element-1397" data-mp="74gbddxggkl0" />
                  <YAxis type="category" dataKey="emirate" width={110} tick={{
                  fontSize: 11,
                  fill: 'var(--muted-foreground)'
                }} tickLine={false} axisLine={false} data-id="element-1398" data-mp="3pb2xwsktddos" />
                  <ChartTooltip contentStyle={{
                  fontSize: 12,
                  borderRadius: 4,
                  border: '1px solid var(--border)'
                }} data-id="element-1399" data-mp="1ik4u1t3oexg0" />
                  <Bar dataKey="records" radius={[0, 2, 2, 0]} data-id="element-1400" data-mp="380qps46upc3m">
                    {emirateData.map((entry, index) => <Cell key={entry.emirate} fill={`var(--chart-${index % 6 + 1})`} data-id="element-1401" data-mp="3cubojyg9qa3v" data-mpi={index} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </SectionCard>
        </div>

        <div className="grid gap-4 xl:grid-cols-3" data-id="element-1402" data-mp="3vr335ukv811l">
          <SupportByStage records={live} data-id="element-1403" data-mp="3vpry7rfoon8w" />
          <RiskWatchlist records={live} data-id="element-1404" data-mp="3gtuamxjtsr8a" />
          <OutreachSnapshot data-id="element-1405" data-mp="3gssnhupjcea7" />
        </div>

        <div className="grid gap-4 xl:grid-cols-2" data-id="element-1406" data-mp="3gw6p30qdkjgl">
          <OfficerWorkload records={live} data-id="element-1407" data-mp="3lrvtpih94qjr" />
          <ActivityFeed records={applications} data-id="element-1408" data-mp="3lv00mnm3q0e4" />
        </div>

        <SectionCard title="Upcoming host-body submission deadlines" description="Bid windows across active Mega and Major records" bodyClassName="p-0" data-id="element-1409" data-mp="25dvvcby218ym">
          <ul className="divide-y divide-border" data-id="element-1410" data-mp="sf2fdyiwo9ze">
            {deadlines.map(({
            record,
            on
          }) => {
            const days = daysUntil(on);
            return <li key={record.id} className="flex flex-wrap items-center justify-between gap-3 px-4 py-3" data-id="element-1411" data-mp="11gjpn09mthg6">
                  <div className="min-w-0" data-id="element-1412" data-mp="xd3vqboy5v76">
                    <p className="truncate text-[13px] font-medium" data-id="element-1413" data-mp="9p47bif3ygvr">{record.eventName}</p>
                    <p className="text-[11px] text-muted-foreground" data-id="element-1414" data-mp="4qsx36kwi4d0">
                      {entityName(record.entityId)} · {record.hostBody}
                    </p>
                  </div>
                  <div className="flex items-center gap-4" data-id="element-1415" data-mp="dr8z0blzx3fr">
                    <span className="text-[12px] text-muted-foreground" data-id="element-1416" data-mp="dpo069sneyc3">{formatDate(on)}</span>
                    <span className={`font-mono text-[12px] ${days < 0 ? 'text-muted-foreground' : days < 120 ? 'text-uae-red' : ''}`} data-id="element-1417" data-mp="3m5hkz0u0ivyy">
                      {days < 0 ? 'submitted' : `${days}d`}
                    </span>
                  </div>
                </li>;
          })}
          </ul>
        </SectionCard>
      </div>
    </div>;
}